/**
 * XRCalibrationPanel — v10.12.0
 * Panel flotante para calibrar el overlay sobre el fantoma físico.
 * Cuatro controles: mover (offset X/Y), tamaño (scale), rotar y opacity.
 *
 * Persiste por (scenario_id, marker_id) vía POST /api/xr/calibrations.
 * Todos los valores son NORMALIZADOS o expresados en unidades relativas
 * al bounding box del marker, para que la calibración sea válida en
 * cualquier dispositivo/resolución/distancia.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowsOutCardinal, ArrowClockwise, ArrowCounterClockwise, MagnifyingGlassPlus, MagnifyingGlassMinus, CircleHalf, FloppyDisk, ArrowUUpLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CAL = { offset_x: 0, offset_y: 0, scale: 1, rotation_deg: 0, opacity: 1 };

export default function XRCalibrationPanel({
  markerId,
  scenarioId,
  accessToken,
  initialCalibration,
  onChange,
  onClose,
}) {
  const [cal, setCal] = useState(() => ({ ...DEFAULT_CAL, ...(initialCalibration || {}) }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCal((prev) => ({ ...prev, ...(initialCalibration || {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerId]);

  useEffect(() => { onChange?.(cal); }, [cal, onChange]);

  const patch = (k, v) => setCal((prev) => ({ ...prev, [k]: v }));
  const step = (k, delta, min, max) => setCal((prev) => {
    const next = Math.max(min, Math.min(max, (prev[k] || 0) + delta));
    return { ...prev, [k]: next };
  });

  const save = async () => {
    if (!markerId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/xr/calibrations`, {
        marker_id: markerId,
        scenario_id: scenarioId || null,
        ...cal,
      }, { headers: { Authorization: `Bearer ${accessToken}` } });
      toast.success('Calibración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setCal(DEFAULT_CAL);
    try {
      await axios.delete(`${API}/api/xr/calibrations`, {
        params: { marker_id: markerId, scenario_id: scenarioId || null },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success('Calibración restablecida');
    } catch { /* silent */ }
  };

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-30 pointer-events-auto"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="xr-calibration-panel"
    >
      <div className="mx-auto max-w-md m-3 rounded-2xl bg-slate-900/85 backdrop-blur-lg border border-cyan-800/80 shadow-2xl">
        <div className="flex items-center justify-between px-3 pt-2.5 pb-2 border-b border-slate-800">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">Calibración RA</div>
            <div className="text-[11px] text-white font-mono truncate">{markerId || '—'}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="xr-cal-close" aria-label="Cerrar"><X size={16} /></button>
        </div>

        <div className="p-3 grid grid-cols-2 gap-3">
          {/* Move */}
          <div className="col-span-2">
            <div className="flex items-center justify-between text-[10px] uppercase text-cyan-300 mb-1">
              <span className="flex items-center gap-1"><ArrowsOutCardinal size={12} weight="bold" /> Mover</span>
              <span className="font-mono text-slate-400">x:{cal.offset_x.toFixed(2)} y:{cal.offset_y.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 place-items-center">
              <span />
              <button onClick={() => step('offset_y', 0.05, -1, 1)} className="w-full h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-up">↑</button>
              <span />
              <button onClick={() => step('offset_x', -0.05, -1, 1)} className="w-full h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-left">←</button>
              <button onClick={() => patch('offset_x', 0) || patch('offset_y', 0)} className="w-full h-8 rounded bg-slate-700 hover:bg-slate-600 text-white text-[10px]" title="Centrar">◎</button>
              <button onClick={() => step('offset_x', 0.05, -1, 1)} className="w-full h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-right">→</button>
              <span />
              <button onClick={() => step('offset_y', -0.05, -1, 1)} className="w-full h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-down">↓</button>
              <span />
            </div>
          </div>

          {/* Size */}
          <div>
            <div className="flex items-center justify-between text-[10px] uppercase text-cyan-300 mb-1">
              <span className="flex items-center gap-1"><MagnifyingGlassPlus size={12} weight="bold" /> Tamaño</span>
              <span className="font-mono text-slate-400">{cal.scale.toFixed(2)}×</span>
            </div>
            <div className="flex gap-1">
              <button onClick={() => step('scale', -0.1, 0.1, 5)} className="flex-1 h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-smaller"><MagnifyingGlassMinus size={14} className="mx-auto" /></button>
              <button onClick={() => step('scale', 0.1, 0.1, 5)} className="flex-1 h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-bigger"><MagnifyingGlassPlus size={14} className="mx-auto" /></button>
            </div>
            <input
              type="range" min="0.1" max="3" step="0.05"
              value={cal.scale}
              onChange={(e) => patch('scale', Number(e.target.value))}
              className="w-full mt-1 accent-cyan-500"
              data-testid="xr-cal-scale-slider"
            />
          </div>

          {/* Rotate */}
          <div>
            <div className="flex items-center justify-between text-[10px] uppercase text-cyan-300 mb-1">
              <span className="flex items-center gap-1"><ArrowClockwise size={12} weight="bold" /> Rotar</span>
              <span className="font-mono text-slate-400">{Math.round(cal.rotation_deg)}°</span>
            </div>
            <div className="flex gap-1">
              <button onClick={() => step('rotation_deg', -15, -720, 720)} className="flex-1 h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-rotate-ccw"><ArrowCounterClockwise size={14} className="mx-auto" /></button>
              <button onClick={() => step('rotation_deg', 15, -720, 720)} className="flex-1 h-8 rounded bg-slate-800 hover:bg-cyan-700 text-white" data-testid="xr-cal-rotate-cw"><ArrowClockwise size={14} className="mx-auto" /></button>
            </div>
            <input
              type="range" min="-180" max="180" step="1"
              value={((cal.rotation_deg + 180) % 360) - 180}
              onChange={(e) => patch('rotation_deg', Number(e.target.value))}
              className="w-full mt-1 accent-cyan-500"
              data-testid="xr-cal-rotation-slider"
            />
          </div>

          {/* Opacity */}
          <div className="col-span-2">
            <div className="flex items-center justify-between text-[10px] uppercase text-cyan-300 mb-1">
              <span className="flex items-center gap-1"><CircleHalf size={12} weight="bold" /> Transparencia</span>
              <span className="font-mono text-slate-400">{Math.round(cal.opacity * 100)}%</span>
            </div>
            <input
              type="range" min="0.1" max="1" step="0.05"
              value={cal.opacity}
              onChange={(e) => patch('opacity', Number(e.target.value))}
              className="w-full accent-cyan-500"
              data-testid="xr-cal-opacity-slider"
            />
          </div>
        </div>

        <div className="p-3 pt-1 border-t border-slate-800 flex items-center gap-2">
          <button onClick={reset} className="flex-1 h-9 rounded bg-slate-800 hover:bg-slate-700 text-white text-xs flex items-center justify-center gap-1" data-testid="xr-cal-reset-btn">
            <ArrowUUpLeft size={12} /> Restablecer
          </button>
          <button onClick={save} disabled={saving || !markerId} className="flex-1 h-9 rounded bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-xs disabled:opacity-50 flex items-center justify-center gap-1" data-testid="xr-cal-save-btn">
            <FloppyDisk size={12} weight="fill" /> {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
